import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { SearchResult } from "../types"
import { useSearch } from "../hooks/useSearch"

interface SearchBoxProps {
  onSearchResult: (result: SearchResult, query: string) => void
  initialSearch?: string | null
}

const SearchBox: React.FC<SearchBoxProps> = ({
  onSearchResult,
  initialSearch,
}) => {
  const { t, i18n } = useTranslation()
  const [searchPlaceholder, setSearchPlaceholder] = useState(
    t("search.placeholder")
  )
  const [isFocused, setIsFocused] = useState(false)
  const { searchInput, setSearchInput, isSearching, performSearch } =
    useSearch()

  const hasSearchedRef = React.useRef(false)

  useEffect(() => {
    if (initialSearch && !hasSearchedRef.current) {
      hasSearchedRef.current = true
      setSearchInput(initialSearch)
      const performInitialSearch = async () => {
        const result = await performSearch(initialSearch)
        if (result) {
          onSearchResult(result, initialSearch)
        }
      }
      performInitialSearch()
    }
  }, [initialSearch, onSearchResult, performSearch, setSearchInput])

  useEffect(() => {
    setSearchPlaceholder(
      t(isFocused ? "search.placeholder.focus" : "search.placeholder")
    )
  }, [i18n.language, isFocused, t])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchInput.trim() || isSearching) return

    const result = await performSearch(searchInput)
    if (result) {
      onSearchResult(result, searchInput)
    }
  }

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div
        className={`card p-6 ${
          isFocused ? "shadow-xl ring-2 ring-blue-100" : "shadow-lg"
        } transition-all duration-300`}
      >
        <h2 className="text-xl font-bold mb-4 hero-text text-center mb-6">
          {t("search.title")}
        </h2>

        <form onSubmit={handleSearch} className="relative">
          <div className="md:flex md:items-center">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              </div>

              <input
                type="text"
                value={searchInput}
                onChange={handleInputChange}
                placeholder={searchPlaceholder}
                className={`w-full input pl-12 pr-12 py-4 text-sm md:text-lg ${
                  isSearching ? "bg-gray-50" : ""
                } md:pr-24`}
                disabled={isSearching}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput("")}
                  className="absolute inset-y-0 right-0 md:right-40 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors "
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              )}
            </div>

            <div className="mt-3 flex justify-center md:mt-0 md:absolute md:inset-y-0 md:right-0 md:flex md:items-center md:pr-2">
              {isSearching ? (
                <div className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin md:mr-8" />
              ) : (
                searchInput && (
                  <button
                    type="submit"
                    className="w-auto py-2 px-6 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white transition hover:opacity-90 md:mr-4"
                  >
                    {t("search.button")}
                  </button>
                )
              )}
            </div>
          </div>
        </form>

        <p className="text-xs text-gray-500 mt-3 text-center">
          {t("search.hint")}
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-3 mt-4">
        <button
          onClick={() => setSearchInput("vitalik.eth")}
          className="text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors duration-200"
        >
          {t("search.autoV")}
        </button>
        <button
          onClick={() => setSearchInput("1")}
          className="text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors duration-200"
        >
          {t("search.autoList")}
        </button>
        <button
          onClick={() =>
            setSearchInput("0x983110309620d911731ac0932219af06091b6744")
          }
          className="text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors duration-200"
        >
          {t("search.autoAddr")}
        </button>
      </div>
    </div>
  )
}

export default SearchBox
